import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import {
  PreferenceKey,
  PreferenceScope,
  SensitiveMetricsPreferences,
} from '../../../data-models/preferences-v3.data-model';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../services/PermissionService';
import { preferenceAtom } from '../../../util/preferences-service-v3';
import { MprColumnConfig } from './MultiPeriodReporting.schemas';
import { isMprMetricsColumnConfig } from './multiPeriodReportingUtils';

export const sensitiveMetricsPrefsState = preferenceAtom<SensitiveMetricsPreferences[]>({
  preferenceKey: `${PreferenceKey.sensitiveMetrics}.$default`,
  scope: PreferenceScope.ORGANIZATION,
});

export function useRemoveSensitiveColumns() {
  const sensitiveFields = useAtomValue(sensitiveMetricsPrefsState);

  return useCallback(
    (columns: MprColumnConfig[]) => {
      if (
        !sensitiveFields?.length ||
        PermissionService.get().hasPermission(PermissionKey.canViewSensitiveMetrics)
      ) {
        return columns;
      }

      const sensitiveFieldsSet = new Set(sensitiveFields);
      return columns.filter((column) => {
        if (!isMprMetricsColumnConfig(column)) {
          return true;
        }
        return !sensitiveFieldsSet.has(column.columnPath as SensitiveMetricsPreferences);
      });
    },
    [sensitiveFields]
  );
}

export function useIsColumnAllowed() {
  const sensitiveFields = useAtomValue(sensitiveMetricsPrefsState);
  const sensitiveFieldsSet = useMemo(() => new Set(sensitiveFields), [sensitiveFields]);

  return useCallback(
    (columnPath: string) => {
      if (sensitiveFieldsSet.size === 0) {
        return true;
      }
      return (
        PermissionService.get().hasPermission(PermissionKey.canViewSensitiveMetrics) ||
        !sensitiveFieldsSet.has(columnPath as SensitiveMetricsPreferences)
      );
    },
    [sensitiveFieldsSet]
  );
}
